import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Database,
  Download,
  FileText,
  FolderKanban,
  ListChecks,
  Network,
  Search,
  UploadCloud,
  X,
} from 'lucide-react';

import {
  DEFAULT_SCAN_PROBES,
  TEMPLATE_URL,
  applyValidatedProbeLabels,
  buildProbeAssignments,
  buildScanReportDocxFileName,
  createFilledDocxFromTemplate,
  downloadBlob,
  downloadTextFile,
  extractIps,
  extractPdfText,
  getProbeLabel,
  parseSaintPdfText,
  remediationFor,
  slugify,
  sourceSummary,
  type GeneratedDraft,
  type ImportedPdfSource,
  type ImportedTextSource,
  type ProbeFilter,
  type ProjectOption,
  type ScanProbe,
  type TargetProbeAssignment,
  type TaskOption,
  type VulnerabilityRow,
} from '../services/vulnerabilityScanDocxGenerator';

interface VulnerabilityScanReportsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ScanTabKey = 'reports' | 'create' | 'vulnerabilities' | 'remediate';

type ScanReportQueueItem = {
  id: string;
  title: string;
  clientName: string;
  clientId?: number | null;
  createdLabel: string;
  status: 'not_converted' | 'project_created';
  projectTitle?: string;
  projectId?: number;
  openTasksCount?: number;
  completedTasksCount?: number;
  criticalOpenCount?: number;
  taskCount?: number;
  sourceDocumentId?: number | null;
  sourceProjectId?: number | null;
  sourceTaskId?: number | null;
  vulnerabilities: VulnerabilityRow[];
};

const scanTabs: Array<{ key: ScanTabKey; label: string; description: string }> = [
  {
    key: 'reports',
    label: 'Rapports créés',
    description: 'Consultez l’ensemble des rapports de balayage déjà générés pour tous les clients.',
  },
  {
    key: 'create',
    label: 'Créer un rapport',
    description: 'Importez un rapport PDF ou TXT, validez les informations détectées et générez le livrable client.',
  },
  {
    key: 'vulnerabilities',
    label: 'Vulnérabilités détectées',
    description: 'Explorez les vulnérabilités extraites des balayages pour prioriser les corrections.',
  },
  {
    key: 'remediate',
    label: 'Créer un projet de correction',
    description: 'Transformez un rapport de balayage en projet de correction avec une tâche par vulnérabilité.',
  },
];

const workflowSteps = [
  {
    title: 'Importer',
    detail: 'PDF complet Carson & Saint ou TXT FortiGate. PRIsME extrait les actifs et vulnérabilités.',
    icon: UploadCloud,
  },
  {
    title: 'Valider',
    detail: 'Confirmer les hôtes, sondes et correspondances d’actifs avant production.',
    icon: Network,
  },
  {
    title: 'Générer',
    detail: 'Créer le rapport client épuré avec le gabarit Word BZ v2025.',
    icon: FileText,
  },
  {
    title: 'Créer un projet',
    detail: 'Chaque vulnérabilité sélectionnée peut devenir une tâche dans un projet de correction dédié.',
    icon: FolderKanban,
  },
];

function severityTone(severity: string) {
  const normalized = severity.toLowerCase();
  if (normalized.includes('crit')) return 'red';
  if (normalized.includes('élev') || normalized.includes('elev') || normalized.includes('high')) return 'amber';
  if (normalized.includes('moy') || normalized.includes('medium')) return 'blue';
  return 'gray';
}

function isIncludedByMinimum(vulnerability: VulnerabilityRow, minimum: string) {
  const severity = vulnerability.severity.toLowerCase();
  if (minimum === 'Critique seulement') return severity.includes('crit');
  if (minimum === 'Critique + élevée') return severity.includes('crit') || severity.includes('élev') || severity.includes('elev') || severity.includes('high');
  if (minimum === 'Critique + élevée + moyenne') return severity.includes('crit') || severity.includes('élev') || severity.includes('elev') || severity.includes('high') || severity.includes('moy') || severity.includes('medium');
  return true;
}

function normalizeRecommendation(row: VulnerabilityRow): VulnerabilityRow {
  return {
    ...row,
    remediation: row.remediation || remediationFor(row.name, row.category),
  };
}

export default function VulnerabilityScanReportsModal({ isOpen, onClose }: VulnerabilityScanReportsModalProps) {
  const [activeTab, setActiveTab] = useState<ScanTabKey>('reports');
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [tasks, setTasks] = useState<TaskOption[]>([]);
  const [persistentScanReports, setPersistentScanReports] = useState<ScanReportQueueItem[]>([]);
  const [loadingPersistentScanReports, setLoadingPersistentScanReports] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [externalClientName, setExternalClientName] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [pdfSource, setPdfSource] = useState<ImportedPdfSource | null>(null);
  const [fortigateSource, setFortigateSource] = useState<ImportedTextSource | null>(null);
  const [generatedDraft, setGeneratedDraft] = useState<GeneratedDraft | null>(null);
  const [createdDocumentId, setCreatedDocumentId] = useState<number | null>(null);
  const [statusMessage, setStatusMessage] = useState('Consultez les rapports créés ou importez un nouveau rapport de balayage.');
  const [scanProbes, setScanProbes] = useState<ScanProbe[]>(DEFAULT_SCAN_PROBES);
  const [probeAssignments, setProbeAssignments] = useState<TargetProbeAssignment[]>([]);
  const [probeFilter, setProbeFilter] = useState<ProbeFilter>('all');
  const [customProbeName, setCustomProbeName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [remediationReportId, setRemediationReportId] = useState('');
  const [remediationPriorityMinimum, setRemediationPriorityMinimum] = useState('Critique + élevée');
  const [remediationDueDate, setRemediationDueDate] = useState('');
  const [creatingRemediationProject, setCreatingRemediationProject] = useState(false);
  const [createdRemediationProject, setCreatedRemediationProject] = useState<{ id: number; title: string } | null>(null);

  const selectedProject = projects.find((project) => String(project.id) === selectedProjectId) || null;
  const selectedTask = tasks.find((task) => String(task.id) === selectedTaskId) || null;
  const remediationClientName = externalClientName.trim() || selectedProject?.nom_client || selectedProject?.title || '';

  const loadPersistentScanReports = async () => {
    setLoadingPersistentScanReports(true);
    try {
      const response = await fetch('/api/prisme/vulnerability-scan-reports', { credentials: 'include' });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.error || 'Impossible de charger les rapports de balayage.');
      const reports = Array.isArray(data?.reports) ? data.reports : [];
      setPersistentScanReports(reports.map((report: any) => ({
        id: String(report.id),
        title: report.title || `Rapport #${report.id}`,
        clientName: report.clientName || report.client_name || 'Client à confirmer',
        clientId: report.clientId || report.client_id || null,
        createdLabel: report.createdLabel || 'Rapport créé',
        status: report.status === 'project_created' || report.remediationProjectId || report.remediation_project_id ? 'project_created' : 'not_converted',
        projectTitle: report.projectTitle || report.remediation_project_title || null,
        projectId: report.projectId || report.remediationProjectId || report.remediation_project_id || null,
        openTasksCount: Number(report.openTasksCount || report.open_tasks_count || 0),
        completedTasksCount: Number(report.completedTasksCount || report.completed_tasks_count || 0),
        criticalOpenCount: Number(report.criticalOpenCount || report.critical_open_count || 0),
        taskCount: Number(report.taskCount || report.task_count || 0),
        sourceDocumentId: report.sourceDocumentId || report.source_document_id || null,
        sourceProjectId: report.sourceProjectId || report.source_project_id || null,
        sourceTaskId: report.sourceTaskId || report.source_task_id || null,
        vulnerabilities: Array.isArray(report.vulnerabilities) ? report.vulnerabilities.map(normalizeRecommendation) : [],
      })));
    } catch (error: any) {
      setStatusMessage(error?.message || 'Impossible de charger les rapports de balayage.');
    } finally {
      setLoadingPersistentScanReports(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingProjects(true);
    fetch('/api/projet-v2/projects', { credentials: 'include' })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Impossible de charger les projets')))
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data.projects || [];
        setProjects(list.map((project: any) => ({
          id: Number(project.id),
          title: project.title || project.nom || `Projet #${project.id}`,
          nom_client: project.nom_client || null,
          idclient: project.idclient || null,
          status: project.status || null,
        })).filter((project: ProjectOption) => Number.isFinite(project.id)));
      })
      .catch((error) => setStatusMessage(error.message || 'Impossible de charger les projets existants.'))
      .finally(() => !cancelled && setLoadingProjects(false));
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    void loadPersistentScanReports();
  }, [isOpen]);

  useEffect(() => {
    if (!selectedProjectId) {
      setTasks([]);
      setSelectedTaskId('');
      return;
    }
    let cancelled = false;
    setLoadingTasks(true);
    fetch(`/api/projet-v2/projects/${selectedProjectId}/tasks`, { credentials: 'include' })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Impossible de charger les tâches du projet')))
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data.tasks || [];
        setTasks(list.map((task: any) => ({ id: Number(task.id), titre: task.titre || task.title || `Tâche #${task.id}`, status_text: task.status_text, isjalon: task.isjalon })).filter((task: TaskOption) => Number.isFinite(task.id)));
        setSelectedTaskId('');
      })
      .catch((error) => setStatusMessage(error.message || 'Impossible de charger les tâches du projet sélectionné.'))
      .finally(() => !cancelled && setLoadingTasks(false));
    return () => { cancelled = true; };
  }, [selectedProjectId]);

  const importedReportVulnerabilities = useMemo(() => (pdfSource?.parsed.recommendations || []).map(normalizeRecommendation), [pdfSource]);

  const scanRemediationReports = useMemo<ScanReportQueueItem[]>(() => {
    const reports = [...persistentScanReports];
    if (pdfSource) {
      reports.unshift({
        id: `imported-${slugify(pdfSource.fileName)}`,
        title: pdfSource.fileName.replace(/\.pdf$/i, ''),
        clientName: remediationClientName || 'Client à confirmer',
        clientId: selectedProject?.idclient || null,
        createdLabel: 'Rapport importé dans cette session',
        status: createdRemediationProject ? 'project_created' : 'not_converted',
        projectTitle: createdRemediationProject?.title,
        projectId: createdRemediationProject?.id,
        vulnerabilities: importedReportVulnerabilities,
      });
    }
    return reports;
  }, [createdRemediationProject, importedReportVulnerabilities, pdfSource, persistentScanReports, remediationClientName, selectedProject?.idclient]);

  const currentRemediationReport = scanRemediationReports.find((report) => report.id === remediationReportId) || scanRemediationReports[0];

  useEffect(() => {
    if (!scanRemediationReports.some((report) => report.id === remediationReportId)) {
      setRemediationReportId(scanRemediationReports[0]?.id || '');
    }
  }, [remediationReportId, scanRemediationReports]);

  const filteredReports = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return scanRemediationReports;
    return scanRemediationReports.filter((report) => [report.title, report.clientName, report.projectTitle]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(term)));
  }, [scanRemediationReports, searchTerm]);

  const allVulnerabilities = useMemo(() => scanRemediationReports.flatMap((report) => report.vulnerabilities.map((vulnerability, index) => ({
    ...normalizeRecommendation(vulnerability),
    reportId: report.id,
    reportTitle: report.title,
    clientName: report.clientName,
    rowKey: `${report.id}-${index}`,
  }))), [scanRemediationReports]);

  const selectedRemediationVulnerabilities = useMemo(() => (currentRemediationReport?.vulnerabilities || [])
    .map(normalizeRecommendation)
    .filter((vulnerability) => isIncludedByMinimum(vulnerability, remediationPriorityMinimum)), [currentRemediationReport, remediationPriorityMinimum]);

  const reportStats = useMemo(() => {
    const notConverted = scanRemediationReports.filter((report) => report.status === 'not_converted');
    return {
      totalReports: scanRemediationReports.length,
      clients: new Set(scanRemediationReports.map((report) => report.clientName)).size,
      notConverted: notConverted.length,
      criticalHigh: notConverted.reduce((total, report) => total + report.vulnerabilities.filter((vulnerability) => isIncludedByMinimum(vulnerability, 'Critique + élevée')).length, 0),
      projectsCreated: scanRemediationReports.filter((report) => report.status === 'project_created').length,
    };
  }, [scanRemediationReports]);

  const importPdfSource = async (file: File | undefined) => {
    if (!file) return;
    setActiveTab('create');
    setStatusMessage('Extraction du PDF complet Carson & Saint...');
    const { text, pageCount, firstPageImageBytes } = await extractPdfText(file);
    const parsed = parseSaintPdfText(text, file.name);
    const source = { fileName: file.name, bytes: file.size, pageCount, firstPageImageBytes, raw: text, parsed };
    setPdfSource(source);
    if (!externalClientName.trim()) {
      setExternalClientName(file.name.replace(/\.pdf$/i, '').replace(/[-_]+/g, ' ').trim());
    }
    setProbeAssignments(buildProbeAssignments(parsed.targets, fortigateSource?.raw || ''));
    setGeneratedDraft(null);
    setCreatedDocumentId(null);
    setRemediationReportId(`imported-${slugify(file.name)}`);
    setStatusMessage(`PDF importé : ${parsed.totalVulnerabilities} vulnérabilités, ${parsed.criticalCount} critiques, ${parsed.highCount} élevées, ${parsed.scannedHosts || parsed.targets.length} hôtes.`);
  };

  const importFortigateSource = async (file: File | undefined) => {
    if (!file) return;
    const raw = await file.text();
    const summary = sourceSummary(raw, file.name);
    setFortigateSource(summary);
    if (pdfSource) setProbeAssignments(buildProbeAssignments(pdfSource.parsed.targets, raw));
    setGeneratedDraft(null);
    setCreatedDocumentId(null);
    setStatusMessage(`TXT FortiGate importé : ${summary.uniqueIpCount} IP uniques détectées pour validation des actifs.`);
  };

  const correlatedIpCount = useMemo(() => {
    if (!pdfSource || !fortigateSource) return 0;
    const fortigateIps = new Set(extractIps(fortigateSource.raw));
    return extractIps(pdfSource.raw).filter((ip) => fortigateIps.has(ip)).length;
  }, [pdfSource, fortigateSource]);

  const probeAssignmentCounts = useMemo(() => ({
    total: probeAssignments.length,
    ready: probeAssignments.filter((assignment) => assignment.status === 'Validé' || assignment.status === 'Corrigé par conseiller').length,
    needsValidation: probeAssignments.filter((assignment) => assignment.status === 'À valider').length,
    unassigned: probeAssignments.filter((assignment) => assignment.status === 'Non assigné' || !assignment.validatedProbeId).length,
    corrected: probeAssignments.filter((assignment) => assignment.status === 'Corrigé par conseiller').length,
  }), [probeAssignments]);

  const probeAssignmentsReady = probeAssignmentCounts.total > 0 && probeAssignmentCounts.needsValidation === 0 && probeAssignmentCounts.unassigned === 0;

  const visibleProbeAssignments = useMemo(() => probeAssignments.filter((assignment) => {
    if (probeFilter === 'needs_validation') return assignment.status === 'À valider';
    if (probeFilter === 'unassigned') return assignment.status === 'Non assigné' || !assignment.validatedProbeId;
    if (probeFilter === 'corrected') return assignment.status === 'Corrigé par conseiller';
    return true;
  }), [probeAssignments, probeFilter]);

  const validatedTargetsForReport = useMemo(() => applyValidatedProbeLabels(probeAssignments, scanProbes), [probeAssignments, scanProbes]);

  const setAssignmentProbe = (targetKey: string, probeId: string, manual = true) => {
    setProbeAssignments((current) => current.map((assignment) => {
      if (assignment.targetKey !== targetKey) return assignment;
      return {
        ...assignment,
        validatedProbeId: probeId,
        status: !probeId ? 'Non assigné' : manual && probeId !== assignment.suggestedProbeId ? 'Corrigé par conseiller' : 'Validé',
      };
    }));
    setGeneratedDraft(null);
    setCreatedDocumentId(null);
  };

  const validateStrongProbeSuggestions = () => {
    setProbeAssignments((current) => current.map((assignment) => assignment.confidence === 'Forte' ? { ...assignment, status: 'Validé' } : assignment));
  };

  const setAssignmentAssetName = (targetKey: string, name: string) => {
    setProbeAssignments((current) => current.map((assignment) => assignment.targetKey === targetKey ? {
      ...assignment,
      target: { ...assignment.target, name },
    } : assignment));
    setGeneratedDraft(null);
    setCreatedDocumentId(null);
  };

  const setAssignmentStatus = (targetKey: string, status: TargetProbeAssignment['status']) => {
    setProbeAssignments((current) => current.map((assignment) => assignment.targetKey === targetKey ? { ...assignment, status } : assignment));
    setGeneratedDraft(null);
    setCreatedDocumentId(null);
  };

  const addCustomProbe = () => {
    const label = customProbeName.trim();
    if (!label) return;
    const id = `probe_custom_${Date.now()}`;
    setScanProbes((current) => [...current, {
      id,
      label,
      probeType: 'custom',
      locationLabel: label,
      scopeLabel: 'Portée personnalisée',
      reportDescription: `${label} utilisée pour une portion spécifique du balayage.`,
    }]);
    setCustomProbeName('');
  };

  const generateDraft = async () => {
    if (!selectedProject) {
      setStatusMessage('Sélectionnez le Projet lié avant de générer le rapport.');
      return;
    }
    if (!selectedTask) {
      setStatusMessage('Sélectionnez la Tâche liée avant de générer le rapport.');
      return;
    }
    if (!pdfSource) {
      setStatusMessage('Importez le PDF complet Carson & Saint avant de générer le rapport.');
      return;
    }
    if (!probeAssignmentsReady) {
      setStatusMessage('Validez les sondes utilisées pour chaque cible avant de générer le rapport client.');
      return;
    }
    const generatedAt = new Date().toLocaleString('fr-CA');
    const client = selectedProject.nom_client || selectedProject.title;
    const summary = pdfSource.parsed;
    const body = `# Rapport de balayage de vulnérabilités — ${client}\n\n` +
      `**Projet lié :** ${selectedProject.title}\n\n` +
      `**Tâche liée :** ${selectedTask.titre}\n\n` +
      `**Généré :** ${generatedAt}\n\n` +
      `## Source importée\n\n` +
      `- PDF complet Carson & Saint : ${pdfSource.fileName} (${pdfSource.pageCount} pages)\n` +
      `- TXT FortiGate : ${fortigateSource ? `${fortigateSource.fileName} (${fortigateSource.uniqueIpCount} IP uniques)` : 'non fourni'}\n\n` +
      `## Synthèse extraite du PDF\n\n` +
      `- Vulnérabilités totales : ${summary.totalVulnerabilities}\n` +
      `- Critiques : ${summary.criticalCount}\n` +
      `- Élevées : ${summary.highCount}\n` +
      `- Moyennes : ${summary.mediumCount}\n` +
      `- Faibles : ${summary.lowCount}\n` +
      `- Score CVSS maximal : ${summary.maxScore.toFixed(1)}\n` +
      `- Hôtes balayés : ${summary.scannedHosts || summary.targets.length}\n` +
      `- Cibles avec sonde validée : ${validatedTargetsForReport.length}\n` +
      `- Correctifs proposés au Tableau 2 : ${Math.min(summary.recommendations.length, 24)}\n`;

    try {
      setStatusMessage('Création du DOCX à partir du gabarit BZ v2025 et du PDF complet...');
      const docxBlob = await createFilledDocxFromTemplate({ project: selectedProject, task: selectedTask, pdfSource, validatedTargetsForReport });
      const docxFileName = buildScanReportDocxFileName(client, summary.monthYear || new Date().toLocaleDateString('fr-CA', { month: 'long', year: 'numeric' }));
      setGeneratedDraft({ title: `rapport-balayage-${slugify(client)}.md`, body, generatedAt, docxFileName, docxBlob });
      downloadBlob(docxFileName, docxBlob);
      setStatusMessage('DOCX rempli généré : champs du gabarit, analyse, conclusion, Tableau 1 et Tableau 2 ont été remplis.');
    } catch (error: any) {
      setGeneratedDraft({ title: `rapport-balayage-${slugify(client)}.md`, body, generatedAt });
      setStatusMessage(error?.message || 'Le brouillon Markdown est prêt, mais le DOCX n’a pas pu être généré.');
    }
  };

  const saveDraft = async () => {
    if (!generatedDraft || !selectedProject) return;
    try {
      const response = await fetch('/api/prisme/vulnerability-scan-reports/document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          projectClientName: selectedProject.nom_client || selectedProject.title,
          projectClientId: selectedProject.idclient || null,
          documentData: {
            document_type_code: 'rapport_balayage_vulnerabilites',
            besoin_id: null,
            quality_level: 1,
            title: `Rapport de balayage de vulnérabilités — ${selectedProject.nom_client || selectedProject.title}`,
            content: { format: 'markdown+docx-template', markdown: generatedDraft.body, template: TEMPLATE_URL, docxFileName: generatedDraft.docxFileName },
            status: 'draft',
            showpriseclient: false,
            presented_client: false,
            archived: false,
            metadata: {
              source: 'vulnerability_scan_reports',
              projectId: selectedProject.id,
              projectTitle: selectedProject.title,
              taskId: selectedTask?.id || null,
              taskTitle: selectedTask?.titre || null,
              projetV2ClientId: selectedProject.idclient || null,
              sourcePdfFileName: pdfSource?.fileName || null,
              fortigateFileName: fortigateSource?.fileName || null,
              vulnerabilities: importedReportVulnerabilities,
              probeAssignments: probeAssignments.map((assignment) => ({
                target: assignment.target.address,
                assetName: assignment.target.name,
                suggestedProbeId: assignment.suggestedProbeId,
                validatedProbeId: assignment.validatedProbeId,
                validatedProbeLabel: getProbeLabel(scanProbes, assignment.validatedProbeId),
                confidence: assignment.confidence,
                status: assignment.status,
              })),
              scanProbes,
              docxGeneratedFromTemplate: true,
              docxFilledInTemplateFields: true,
            },
          },
        }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) throw new Error(result?.error || 'Impossible de créer le document dans Gestion documentaire.');
      const document = result?.data || result;
      setCreatedDocumentId(document.id);
      if (result?.scanReport?.id) setRemediationReportId(String(result.scanReport.id));
      await loadPersistentScanReports();
      setStatusMessage(`Document créé dans Gestion documentaire (#${document.id}) pour le client ${result?.documentClient?.nom_client || selectedProject.nom_client || selectedProject.title}.`);
    } catch (error: any) {
      setStatusMessage(error?.message || 'Impossible de créer le document dans Gestion documentaire. Le DOCX rempli reste téléchargeable.');
    }
  };

  const createRemediationProject = async () => {
    if (!currentRemediationReport) return;
    if (selectedRemediationVulnerabilities.length === 0) {
      setStatusMessage('Aucune vulnérabilité ne correspond au filtre choisi pour créer le projet.');
      return;
    }
    if (!currentRemediationReport.clientName || currentRemediationReport.clientName === 'Client à confirmer') {
      setStatusMessage('Indiquez le client du rapport avant de créer le projet de correction.');
      setActiveTab('create');
      return;
    }
    setCreatingRemediationProject(true);
    setStatusMessage('Création du projet de correction et des tâches de vulnérabilités...');
    try {
      const response = await fetch('/api/prisme/vulnerability-scan-reports/remediation-project', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientName: currentRemediationReport.clientName,
          clientId: currentRemediationReport.clientId || null,
          reportTitle: currentRemediationReport.title,
          sourceReportId: currentRemediationReport.id,
          priorityMinimum: remediationPriorityMinimum,
          dueDate: remediationDueDate,
          vulnerabilities: selectedRemediationVulnerabilities.map((vulnerability) => ({ ...vulnerability, selected: true })),
        }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) throw new Error(result?.error || 'Impossible de créer le projet de correction.');
      const project = result.project;
      setCreatedRemediationProject({ id: Number(project.id), title: project.title });
      await loadPersistentScanReports();
      setStatusMessage(result.created
        ? `Projet de correction créé : ${project.title}. ${result.taskCount || selectedRemediationVulnerabilities.length} tâches générées.`
        : `Projet de correction déjà existant : ${project.title}.`);
      setActiveTab('remediate');
    } catch (error: any) {
      setStatusMessage(error?.message || 'Impossible de créer le projet de correction.');
    } finally {
      setCreatingRemediationProject(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-3 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <motion.div className="flex max-h-[94vh] w-full max-w-[min(96vw,1400px)] flex-col overflow-hidden rounded-3xl bg-white shadow-2xl" initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }} transition={{ duration: 0.2 }}>
            <div className="border-b border-slate-200 bg-gradient-to-r from-blue-950 via-blue-900 to-blue-700 px-6 py-5 text-white">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.28em] text-blue-200">Solutions d’affaires → Cyber → Outil interne</p>
                  <h2 className="mt-2 text-2xl font-black tracking-tight md:text-3xl">Balayages de vulnérabilités</h2>
                  <p className="mt-2 max-w-4xl text-sm leading-6 text-blue-100">Générer les rapports, retrouver l’historique client, explorer les vulnérabilités et créer un projet de correction dédié.</p>
                </div>
                <button type="button" onClick={onClose} className="rounded-full border border-white/20 bg-white/10 p-2 text-white transition hover:bg-white/20" aria-label="Fermer Balayages de vulnérabilités"><X className="h-5 w-5" /></button>
              </div>
              <nav className="mt-5 flex flex-wrap gap-2">
                {scanTabs.map((tab) => (
                  <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} className={`rounded-xl border px-4 py-2 text-sm font-black transition ${activeTab === tab.key ? 'border-white bg-white text-blue-900' : 'border-white/20 bg-white/10 text-white hover:bg-white/20'}`}>
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="overflow-y-auto overflow-x-hidden bg-slate-50 p-5">
              <section className="mb-4 grid gap-3 lg:grid-cols-[1.3fr_repeat(4,minmax(0,1fr))]">
                <KpiCard label="À traiter" value={`${reportStats.notConverted} rapports`} detail="Rapports générés sans projet de correction associé" highlight />
                <KpiCard label="Rapports" value={String(reportStats.totalReports)} detail="Créés dans l’outil" />
                <KpiCard label="Clients" value={String(reportStats.clients)} detail="Avec au moins un rapport" />
                <KpiCard label="Critiques / élevées" value={String(reportStats.criticalHigh)} detail="Dans les rapports non convertis" />
                <KpiCard label="Projets créés" value={String(reportStats.projectsCreated)} detail="Projets de correction générés" />
              </section>

              <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_18rem]">
                <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5 min-w-0">
                  <div className="border-b border-slate-200 px-5 py-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="text-xl font-black text-slate-950">{scanTabs.find((tab) => tab.key === activeTab)?.label}</h3>
                        <p className="mt-1 text-sm text-slate-600">{scanTabs.find((tab) => tab.key === activeTab)?.description}</p>
                      </div>
                      {activeTab === 'reports' ? (
                        <div className="relative min-w-[280px]">
                          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                          <input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Rechercher client, projet, rapport…" className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-300" />
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {activeTab === 'reports' ? (
                    <div className="p-4">
                      {loadingPersistentScanReports ? <p className="mb-3 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-bold text-blue-800">Chargement de l’historique persistant des rapports…</p> : null}
                      <div className="grid gap-3">
                        {filteredReports.length ? filteredReports.map((report) => {
                          const critical = report.vulnerabilities.filter((vulnerability) => severityTone(vulnerability.severity) === 'red').length;
                          const high = report.vulnerabilities.filter((vulnerability) => severityTone(vulnerability.severity) === 'amber').length;
                          return (
                            <article key={report.id} className={`grid gap-3 rounded-2xl border p-4 transition hover:-translate-y-0.5 hover:shadow-md lg:grid-cols-[auto_1.25fr_0.7fr_0.9fr_0.8fr_auto] ${report.id === currentRemediationReport?.id ? 'border-blue-300 bg-blue-50/40 ring-2 ring-blue-100' : 'border-slate-200 bg-white'}`}>
                              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-blue-50 text-blue-700"><FileText className="h-5 w-5" /></div>
                              <div><p className="font-black text-slate-950">{report.title}</p><p className="text-xs text-slate-500">Client : {report.clientName} · {report.createdLabel}</p></div>
                              <div><Badge tone={report.status === 'project_created' ? 'green' : 'amber'}>{report.status === 'project_created' ? 'Projet créé' : 'Correction non créée'}</Badge></div>
                              <div className="flex flex-wrap gap-1"><Badge tone="red">{critical} critiques</Badge><Badge tone="amber">{high} élevées</Badge>{report.criticalOpenCount ? <Badge tone="red">{report.criticalOpenCount} critiques ouvertes</Badge> : null}</div>
                              <div><p className="font-black text-slate-950">{report.vulnerabilities.length} vuln.</p><p className="text-xs text-slate-500">{report.projectTitle || 'Prêt à créer un projet'}</p>{report.status === 'project_created' ? <p className="mt-1 text-xs font-bold text-slate-700">{report.openTasksCount || 0} tâches ouvertes · {report.completedTasksCount || 0} terminées</p> : null}</div>
                              <button type="button" onClick={() => { setRemediationReportId(report.id); if (report.status === 'project_created' && report.projectId) window.open(`/projets-v2/${report.projectId}`, '_blank', 'noopener,noreferrer'); else setActiveTab('remediate'); }} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 hover:border-blue-300 hover:text-blue-800">{report.status === 'project_created' ? 'Voir projet' : 'Créer projet'}</button>
                            </article>
                          );
                        }) : (
                          <div className="rounded-2xl border border-dashed border-blue-200 bg-white p-6 text-center text-sm text-slate-600">
                            Aucun rapport de balayage réel n’est chargé pour l’instant. Importez un PDF dans <b>Créer un rapport</b> pour alimenter l’historique de session et créer un projet de correction.
                          </div>
                        )}
                      </div>
                    </div>
                  ) : activeTab === 'create' ? (
                    <div className="p-5">
                      <div className="grid gap-3 md:grid-cols-4">
                        {workflowSteps.map((step, index) => {
                          const Icon = step.icon;
                          const active = (index === 0 && pdfSource) || (index >= 1 && generatedDraft);
                          return (
                            <div key={step.title} className={`rounded-2xl border p-4 shadow-sm ${active ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-white'}`}>
                              <div className="mb-3 flex items-center justify-between"><span className="grid h-8 w-8 place-items-center rounded-full bg-blue-50 text-sm font-black text-blue-700">{index + 1}</span><Icon className="h-5 w-5 text-slate-400" /></div>
                              <h4 className="font-black text-slate-900">{step.title}</h4>
                              <p className="mt-2 text-xs leading-5 text-slate-600">{step.detail}</p>
                            </div>
                          );
                        })}
                      </div>

                      <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1fr]">
                        <div className="rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50/50 p-6 text-center">
                          <UploadCloud className="mx-auto h-10 w-10 text-blue-700" />
                          <h4 className="mt-3 text-lg font-black text-slate-950">Créer un rapport de balayage</h4>
                          <p className="mt-1 text-sm font-bold text-blue-800">Importer PDF complet Carson & Saint</p>
                          <p className="mt-2 text-sm text-slate-600">Déposer le PDF ou sélectionner un fichier. PRIsME extrait les hôtes, sévérités, vulnérabilités et correctifs.</p>
                          <label className="mt-4 inline-flex cursor-pointer rounded-xl bg-blue-700 px-5 py-3 text-sm font-black text-white hover:bg-blue-800">
                            <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={(event) => void importPdfSource(event.target.files?.[0])} />
                            Importer PDF complet Carson & Saint
                          </label>
                          <p className="mt-3 text-xs text-slate-500">{pdfSource ? `${pdfSource.fileName} — ${pdfSource.parsed.totalVulnerabilities} vulnérabilités` : 'Aucun PDF importé'}</p>
                        </div>
	                        <div className="space-y-3">
	                          <div className="grid gap-3 md:grid-cols-2">
	                            <label className="text-sm font-bold text-slate-700 md:col-span-2">Client du rapport externe
	                              <input value={externalClientName} onChange={(event) => setExternalClientName(event.target.value)} placeholder="Nom du client à utiliser pour créer le projet" className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-normal outline-none focus:border-blue-300" />
	                            </label>
	                            <label className="text-sm font-bold text-slate-700">Projet lié
	                              <select value={selectedProjectId} onChange={(event) => setSelectedProjectId(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-normal outline-none focus:border-blue-300">
	                                <option value="">{loadingProjects ? 'Chargement des projets...' : 'Sélectionner un projet existant'}</option>
                                {projects.map((project) => <option key={project.id} value={project.id}>{project.title}{project.nom_client ? ` — ${project.nom_client}` : ''}</option>)}
                              </select>
                            </label>
                            <label className="text-sm font-bold text-slate-700">Tâche liée
                              <select value={selectedTaskId} onChange={(event) => setSelectedTaskId(event.target.value)} disabled={!selectedProjectId || loadingTasks} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-normal outline-none focus:border-blue-300 disabled:opacity-50">
                                <option value="">{!selectedProjectId ? 'Sélectionnez d’abord un projet' : loadingTasks ? 'Chargement des tâches...' : 'Sélectionner une tâche du projet'}</option>
                                {tasks.map((task) => <option key={task.id} value={task.id}>{task.titre}{task.status_text ? ` — ${task.status_text}` : ''}</option>)}
                              </select>
                            </label>
                          </div>
                          <label className="block cursor-pointer rounded-2xl border border-emerald-200 bg-white p-4 transition hover:bg-emerald-50">
                            <input type="file" accept=".txt,text/plain" className="hidden" onChange={(event) => void importFortigateSource(event.target.files?.[0])} />
                            <span className="flex items-center gap-2 font-bold text-emerald-800"><Database className="h-5 w-5" /> Importer TXT FortiGate</span>
                            <span className="mt-2 block text-sm text-slate-600">{fortigateSource ? fortigateSource.fileName : 'Optionnel pour comparer les actifs'}</span>
                          </label>
                          <div className="flex flex-wrap gap-2">
                            <button type="button" onClick={() => void generateDraft()} disabled={!pdfSource || !selectedProjectId || !selectedTaskId || !probeAssignmentsReady} className="inline-flex items-center gap-2 rounded-full bg-blue-700 px-5 py-3 text-sm font-bold text-white transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50">Générer le DOCX <ArrowRight className="h-4 w-4" /></button>
                            <button type="button" onClick={() => void saveDraft()} disabled={!generatedDraft} className="rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:border-blue-300 disabled:cursor-not-allowed disabled:opacity-50">Créer dans Gestion documentaire</button>
                          </div>
                        </div>
                      </div>

                      <section className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div><h4 className="text-base font-bold text-slate-900">Valider les sondes et cibles — Validation des actifs — Projet / tâche / livrable</h4><p className="mt-1 text-sm text-slate-600">PRIsME propose une association, mais le conseiller peut corriger ligne par ligne ou en lot avant génération.</p></div>
                          <div className="flex flex-wrap gap-2 text-xs font-bold"><Badge tone="green">{probeAssignmentCounts.ready} validées</Badge><Badge tone="amber">{probeAssignmentCounts.needsValidation} à valider</Badge><Badge tone="blue">{probeAssignmentCounts.corrected} corrigées</Badge></div>
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                          <button type="button" onClick={validateStrongProbeSuggestions} disabled={!probeAssignments.length} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-800 disabled:opacity-50">Valider les propositions fortes</button>
                          <button type="button" onClick={() => setProbeFilter('needs_validation')} className={`rounded-full px-3 py-2 text-xs font-bold ${probeFilter === 'needs_validation' ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-800'}`}>Filtrer : À valider</button>
                          <button type="button" onClick={() => setProbeFilter('unassigned')} className={`rounded-full px-3 py-2 text-xs font-bold ${probeFilter === 'unassigned' ? 'bg-rose-600 text-white' : 'bg-rose-50 text-rose-800'}`}>Non assignées</button>
                          <button type="button" onClick={() => setProbeFilter('all')} className="rounded-full bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700">Tout afficher</button>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2 rounded-xl bg-slate-50 p-3">
                          <input value={customProbeName} onChange={(event) => setCustomProbeName(event.target.value)} placeholder="Nom de la sonde personnalisée" className="min-w-[220px] flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-300" />
                          <button type="button" onClick={addCustomProbe} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white">Ajouter une sonde</button>
                        </div>
                        <div className="mt-4 max-h-72 overflow-y-auto overflow-x-hidden rounded-xl border border-slate-200">
                          <table className="w-full table-fixed divide-y divide-slate-200 text-sm">
                            <thead className="sticky top-0 bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600"><tr><th className="w-28 px-2 py-2">Cible</th><th className="px-2 py-2">Actif TI</th><th className="w-28 px-2 py-2">Proposée</th><th className="w-40 px-2 py-2">Validée</th><th className="w-36 px-2 py-2">Statut</th></tr></thead>
                            <tbody className="divide-y divide-slate-100 bg-white">
                              {visibleProbeAssignments.length ? visibleProbeAssignments.map((assignment) => (
                                <tr key={assignment.targetKey}>
                                  <td className="break-all px-2 py-2 font-mono text-xs text-slate-900">{assignment.target.address}</td>
                                  <td className="px-2 py-2"><input value={assignment.target.name} onChange={(event) => setAssignmentAssetName(assignment.targetKey, event.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-900 outline-none focus:border-blue-300" placeholder="Nom de l’actif TI" /><span className="mt-1 block text-xs text-slate-500">{assignment.target.functionLabel}</span></td>
                                  <td className="px-2 py-2 text-xs text-slate-600">{getProbeLabel(scanProbes, assignment.suggestedProbeId)}<br />Conf. : {assignment.confidence}</td>
                                  <td className="px-2 py-2"><select value={assignment.validatedProbeId} onChange={(event) => setAssignmentProbe(assignment.targetKey, event.target.value, true)} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs outline-none focus:border-blue-300"><option value="">À choisir</option>{scanProbes.map((probe) => <option key={probe.id} value={probe.id}>{probe.label}</option>)}</select></td>
                                  <td className="px-2 py-2"><select value={assignment.status} onChange={(event) => setAssignmentStatus(assignment.targetKey, event.target.value as TargetProbeAssignment['status'])} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-bold outline-none focus:border-blue-300"><option value="Validé">Validé</option><option value="À valider">À valider</option><option value="Corrigé par conseiller">Corrigé</option><option value="Non assigné">Non assigné</option></select></td>
                                </tr>
                              )) : <tr><td colSpan={5} className="px-3 py-5 text-center text-sm text-slate-500">Importez le PDF complet pour voir les cibles scannées et les sondes proposées.</td></tr>}
                            </tbody>
                          </table>
                        </div>
                      </section>

                      {generatedDraft ? <DraftOutput generatedDraft={generatedDraft} onDownloadDocx={() => generatedDraft.docxBlob && downloadBlob(generatedDraft.docxFileName || 'rapport-balayage.docx', generatedDraft.docxBlob)} onDownloadMarkdown={() => downloadTextFile(generatedDraft.title, generatedDraft.body)} /> : null}
                    </div>
                  ) : activeTab === 'vulnerabilities' ? (
                    <div className="overflow-auto p-4">
                      <table className="w-full border-separate border-spacing-y-2 text-sm">
                        <thead className="text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-3">Priorité</th><th className="px-3">Vulnérabilité</th><th className="px-3">Actif / Hôte</th><th className="px-3">Client</th><th className="px-3">Correctif proposé</th><th className="px-3">État correction</th></tr></thead>
                        <tbody>
                          {allVulnerabilities.length ? allVulnerabilities.map((vulnerability) => {
                            const report = scanRemediationReports.find((item) => item.id === vulnerability.reportId);
                            return (
                              <tr key={vulnerability.rowKey}>
                                <td className="rounded-l-2xl border-y border-l border-slate-200 bg-white px-3 py-3"><Badge tone={severityTone(vulnerability.severity)}>{vulnerability.severity}</Badge></td>
                                <td className="border-y border-slate-200 bg-white px-3 py-3"><p className="font-black text-slate-950">{vulnerability.name}</p><p className="text-xs text-slate-500">{vulnerability.category || vulnerability.reportTitle}</p></td>
                                <td className="border-y border-slate-200 bg-white px-3 py-3">{vulnerability.ip || 'À confirmer'}{vulnerability.port ? <p className="text-xs text-slate-500">{vulnerability.port}</p> : null}</td>
                                <td className="border-y border-slate-200 bg-white px-3 py-3">{vulnerability.clientName}</td>
                                <td className="border-y border-slate-200 bg-white px-3 py-3 text-slate-600">{vulnerability.remediation}</td>
                                <td className="rounded-r-2xl border-y border-r border-slate-200 bg-white px-3 py-3"><Badge tone={report?.status === 'project_created' ? 'green' : 'amber'}>{report?.status === 'project_created' ? 'Projet créé' : 'Non envoyée'}</Badge></td>
                              </tr>
                            );
                          }) : (
                            <tr><td colSpan={6} className="rounded-2xl border border-dashed border-blue-200 bg-white px-4 py-8 text-center text-sm text-slate-600">Aucune vulnérabilité réelle à afficher. Importez un rapport PDF pour remplir cette vue.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="grid gap-4 p-4 lg:grid-cols-[24rem_minmax(0,1fr)]">
                      <aside className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="mb-3 flex items-center justify-between gap-2"><h4 className="font-black text-slate-950">Rapports à transformer</h4><Badge tone="amber">{reportStats.notConverted} non envoyés</Badge></div>
                        <div className="space-y-2">
                          {scanRemediationReports.length ? scanRemediationReports.map((report) => (
                            <button key={report.id} type="button" onClick={() => setRemediationReportId(report.id)} className={`w-full rounded-2xl border p-3 text-left transition ${currentRemediationReport?.id === report.id ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100' : report.status === 'project_created' ? 'border-slate-200 bg-white opacity-70' : 'border-slate-200 bg-white hover:border-blue-200'}`}>
                              <Badge tone={report.status === 'project_created' ? 'green' : 'amber'}>{report.status === 'project_created' ? 'Projet créé' : 'Non envoyé'}</Badge>
                              <p className="mt-2 font-black text-slate-950">{report.title}</p>
                              <p className="mt-1 text-xs text-slate-500">Client : {report.clientName} · {report.vulnerabilities.length} vulnérabilités</p>
                            </button>
                          )) : <div className="rounded-2xl border border-dashed border-amber-200 bg-white p-4 text-sm text-slate-600">Aucun rapport réel à transformer. Les rapports importés apparaîtront ici.</div>}
                        </div>
                      </aside>
                      <section className="min-w-0 space-y-4">
                        <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-5">
                          <Badge tone="purple">Nouveau projet</Badge>
                          <h4 className="mt-2 text-2xl font-black tracking-tight text-slate-950">Correction des vulnérabilités — {currentRemediationReport?.clientName}</h4>
                          <p className="mt-2 text-sm text-slate-600">Un nouveau projet dédié sera créé. Chaque vulnérabilité sélectionnée deviendra une tâche distincte avec actif, sévérité, correctif et source du rapport.</p>
                          <div className="mt-4 grid gap-3 md:grid-cols-3">
                            <label className="text-sm font-bold text-slate-700">Attribuer le projet à<select className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-normal"><option>À attribuer / équipe cybersécurité BZ</option><option>Conseiller responsable</option><option>Technicien assigné</option></select></label>
                            <label className="text-sm font-bold text-slate-700">Priorité minimale<select value={remediationPriorityMinimum} onChange={(event) => setRemediationPriorityMinimum(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-normal"><option>Critique + élevée</option><option>Critique seulement</option><option>Critique + élevée + moyenne</option><option>Toutes les vulnérabilités</option></select></label>
                            <label className="text-sm font-bold text-slate-700">Échéance cible<input type="date" value={remediationDueDate} onChange={(event) => setRemediationDueDate(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-normal" /></label>
                          </div>
                          <div className="mt-4 grid gap-3 md:grid-cols-2"><KpiCard label="Tâches à créer" value={String(selectedRemediationVulnerabilities.length)} detail="Selon la priorité minimale" /><KpiCard label="Source" value={currentRemediationReport?.title || '—'} detail="Rapport de balayage sélectionné" /></div>
                        </div>
                        <div className="flex flex-wrap items-end justify-between gap-3">
                          <div><h4 className="text-lg font-black text-slate-950">Tâches générées à partir du rapport</h4><p className="text-sm text-slate-600">Aucune tâche n’est créée avant confirmation. La création se fera dans un nouveau projet dédié.</p></div>
                          <div className="flex flex-wrap gap-2"><Badge tone="red">{selectedRemediationVulnerabilities.filter((v) => severityTone(v.severity) === 'red').length} critiques</Badge><Badge tone="amber">{selectedRemediationVulnerabilities.filter((v) => severityTone(v.severity) === 'amber').length} élevées</Badge><Badge tone="gray">{Math.max(0, (currentRemediationReport?.vulnerabilities.length || 0) - selectedRemediationVulnerabilities.length)} exclues</Badge></div>
                        </div>
                        <div className="space-y-2">
                          {selectedRemediationVulnerabilities.map((vulnerability, index) => (
                            <div key={`${vulnerability.ip}-${vulnerability.name}-${index}`} className="grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                              <div className="grid h-6 w-6 place-items-center rounded-lg bg-blue-700 text-xs font-black text-white">✓</div>
                              <div><p className="font-black text-slate-950">VULN-{String(index + 1).padStart(3, '0')} · {vulnerability.name}</p><p className="text-sm text-slate-600">Actif : {vulnerability.ip || 'À confirmer'} · Correctif : {vulnerability.remediation}</p></div>
                              <Badge tone={severityTone(vulnerability.severity)}>{vulnerability.severity}</Badge>
                            </div>
                          ))}
                        </div>
                        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900"><b>Résultat attendu :</b> après création, le rapport passe à l’état <b>Projet créé</b>, avec un lien direct vers le projet de correction et les tâches générées.</div>
                        <button type="button" onClick={() => void createRemediationProject()} disabled={creatingRemediationProject || !currentRemediationReport || selectedRemediationVulnerabilities.length === 0 || currentRemediationReport?.status === 'project_created'} className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50">
                          <ListChecks className="h-4 w-4" /> {creatingRemediationProject ? 'Création…' : 'Créer le projet + les tâches'}
                        </button>
                      </section>
                    </div>
                  )}
                </section>

                <aside className="space-y-4 min-w-0">
                  <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h3 className="font-black text-slate-950">Logique UX proposée</h3>
                    <InfoBox title="1. Rapports non envoyés">La correction commence par une file de rapports qui n’ont pas encore de projet associé.</InfoBox>
                    <InfoBox title="2. Nouveau projet dédié">On ne pousse pas dans un projet existant. L’outil crée un projet de correction propre au rapport.</InfoBox>
                    <InfoBox title="3. Une tâche par vulnérabilité">Chaque vulnérabilité sélectionnée devient une tâche traçable avec actif, sévérité et correctif.</InfoBox>
                  </section>
                  <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h3 className="font-black text-slate-950">Rapport sélectionné</h3>
                    <div className="mt-3 space-y-2 text-sm">
                      <Metric label="Client" value={currentRemediationReport?.clientName || '—'} />
                      <Metric label="Rapport" value={currentRemediationReport?.title || '—'} />
                      <Metric label="État correction" value={currentRemediationReport?.status === 'project_created' ? 'Projet créé' : 'Non envoyé'} />
                      <Metric label="Tâches prévues" value={String(selectedRemediationVulnerabilities.length)} />
                      <Metric label="Projet" value={createdRemediationProject?.title || currentRemediationReport?.projectTitle || '—'} />
                      <Metric label="Correspondances" value={fortigateSource && pdfSource ? `${correlatedIpCount} / ${extractIps(pdfSource.raw).length}` : '—'} />
                    </div>
                  </section>
                  <section className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900"><p className="font-black">État</p><p className="mt-1 leading-6">{statusMessage}</p>{createdDocumentId && <p className="mt-2 font-bold">Document Gestion documentaire #{createdDocumentId}</p>}</section>
                  <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900"><div className="flex items-start gap-3"><AlertTriangle className="mt-1 h-5 w-5 shrink-0" /><p className="text-sm leading-6"><b>Validation obligatoire.</b> Les actifs, intitulés et correctifs doivent être relus avant remise client ou création de tâches opérationnelles.</p></div></section>
                </aside>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Badge({ tone = 'blue', children }: { tone?: string; children: React.ReactNode }) {
  const classes: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-700',
    red: 'bg-rose-50 text-rose-700',
    amber: 'bg-amber-50 text-amber-800',
    green: 'bg-emerald-50 text-emerald-700',
    purple: 'bg-purple-50 text-purple-700',
    gray: 'bg-slate-100 text-slate-600',
  };
  return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-black ${classes[tone] || classes.blue}`}>{children}</span>;
}

function KpiCard({ label, value, detail, highlight = false }: { label: string; value: string; detail: string; highlight?: boolean }) {
  return <div className={`rounded-2xl border p-4 shadow-sm ${highlight ? 'border-blue-200 bg-gradient-to-br from-white to-blue-50' : 'border-slate-200 bg-white'}`}><p className="text-[11px] font-black uppercase tracking-wide text-blue-700">{label}</p><p className="mt-1 text-2xl font-black text-slate-950">{value}</p><p className="mt-1 text-xs text-slate-500">{detail}</p></div>;
}

function Metric({ label, value }: { label: string; value: string }) {
  return <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2"><span className="text-slate-600">{label}</span><strong className="truncate text-right text-slate-950">{value}</strong></div>;
}

function InfoBox({ title, children }: { title: string; children: React.ReactNode }) {
  return <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3"><h4 className="font-black text-slate-900">{title}</h4><p className="mt-1 text-sm leading-6 text-slate-600">{children}</p></div>;
}

function DraftOutput({ generatedDraft, onDownloadDocx, onDownloadMarkdown }: { generatedDraft: GeneratedDraft; onDownloadDocx: () => void; onDownloadMarkdown: () => void }) {
  return (
    <section className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex items-start justify-between gap-3"><div><h3 className="text-lg font-bold text-slate-900">Brouillon généré</h3><p className="text-sm text-slate-500">Généré le {generatedDraft.generatedAt}. Le DOCX rempli provient du gabarit BZ v2025.</p></div><CheckCircle2 className="h-6 w-6 text-emerald-600" /></div>
      <div className="mb-3 flex flex-wrap gap-2">{generatedDraft.docxBlob ? <button type="button" onClick={onDownloadDocx} className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-800"><Download className="h-4 w-4" /> Télécharger DOCX rempli</button> : null}<button type="button" onClick={onDownloadMarkdown} className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-bold text-blue-800"><Download className="h-4 w-4" /> Télécharger Markdown</button></div>
      <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-2xl bg-slate-950 p-4 text-xs leading-5 text-slate-100">{generatedDraft.body}</pre>
    </section>
  );
}
